import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faFilter, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { useBusinessData } from '../../context/BusinessContext';
import '../../css/Products.css';

const Products = () => {
  const { loading, error, getData, getImageUrl } = useBusinessData();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [filteredProducts, setFilteredProducts] = useState([]);

  // Get products data
  const productsConfig = getData('products', {});
  const products = productsConfig.items || [];
  const categories = ['All', ...(productsConfig.categories || [])];
  
  // Get background image for hero section
  const heroImage = getImageUrl('images.backgrounds.products', 'services-hero.jpg'); // Fallback to services hero

  useEffect(() => {
    // Filter products based on search term and category
    let result = products;

    if (selectedCategory !== 'All') {
      result = result.filter(product => product.category === selectedCategory);
    }

    if (searchTerm) {
      const lowerTerm = searchTerm.toLowerCase();
      result = result.filter(product => 
        product.name.toLowerCase().includes(lowerTerm) || 
        product.shortDescription.toLowerCase().includes(lowerTerm) ||
        product.category.toLowerCase().includes(lowerTerm)
      );
    }

    setFilteredProducts(result);
  }, [products, searchTerm, selectedCategory]);

  if (loading) return <div className="loading">Loading...</div>;
  if (error) return <div className="error">Error loading data: {error}</div>;
  
  // If products feature is disabled, don't show page content
  if (productsConfig.enabled === false) {
    return (
      <div className="container" style={{ padding: '50px 0', textAlign: 'center' }}>
        <h2>Product catalog is currently unavailable.</h2>
        <Link to="/" className="btn btn-primary">Return Home</Link>
      </div>
    );
  }

  const heroStyle = {
    backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url('${heroImage}')`
  };

  return (
    <div className="products-page">
      {/* Hero Section */}
      <div className="products-hero" style={heroStyle}>
        <div className="container">
          <h1>{productsConfig.pageTitle || 'Our Products'}</h1>
          <p>{productsConfig.pageSubtitle || 'Explore our wide range of offerings'}</p>
        </div>
      </div>

      <div className="container">
        {/* Controls Section */}
        <div className="products-controls">
          <div className="search-bar">
            <FontAwesomeIcon icon={faSearch} />
            <input 
              type="text" 
              placeholder="Search products..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="categories-filter">
            {categories.map((category) => (
              <button
                key={category}
                className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Products Grid */}
        {filteredProducts.length > 0 ? (
          <div className="products-grid">
            {filteredProducts.map((product) => {
              // Get first image as thumbnail, or placeholder
              const thumbnail = product.images && product.images.length > 0 
                ? product.images[0] 
                : 'https://via.placeholder.com/400x300?text=No+Image';

              return (
                <div className="product-card" key={product.id}>
                  <div className="product-image-container">
                    <img src={thumbnail} alt={product.name} className="product-image" />
                    <span className="category-badge">{product.category}</span>
                  </div>
                  <div className="product-info">
                    <h3>{product.name}</h3>
                    <p className="product-description">{product.shortDescription}</p>
                    <div className="product-meta">
                      <span className="product-price">
                        {product.price && !isNaN(product.price) 
                          ? `${productsConfig.currency || ''} ${product.price}`
                          : product.price}
                      </span>
                      <Link to={`/products/${product.id}`} className="btn-view-product">
                        View Details <FontAwesomeIcon icon={faArrowRight} />
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="no-results" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <h3>No products found matching your criteria.</h3>
            <button 
              className="btn-text" 
              onClick={() => {setSearchTerm(''); setSelectedCategory('All');}}
              style={{ marginTop: '10px', textDecoration: 'underline', background: 'none', border: 'none', color: 'var(--primary-color)', cursor: 'pointer' }}
            >
              Clear filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Products;
